import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, RotateCcw, MapPin, AlertTriangle, CheckCircle, Eye, Clock, User } from "lucide-react";

const POI_REF = "https://static.prod-images.emergentagent.com/jobs/88bcfbb5-0f75-48df-9f1d-e41b46909d27/images/dee92f3bd56e4f087c8f9f21fdb3cf2a2c4b12bd8d69d783de02d33878231fbe.png";

const scenarios = [
  {
    id: "clear",
    label: "Clear Face — High Confidence",
    image: "https://static.prod-images.emergentagent.com/jobs/88bcfbb5-0f75-48df-9f1d-e41b46909d27/images/01a7f45f1606291a072ba361fc20e54a9d879f9e8e90a14770622484e08fcedf.png",
    score: 87,
    camera: "ANPR-CAM-07 | Sector 12, Gate B",
    time: "14:37:22",
    location: "26.8506 N, 80.9467 E",
    action: "auto",
    description: "Subject detected with clear facial features. High-confidence match triggers automatic geo-tagging and real-time tracking.",
    features: { face_visible: "Full", angle: "Frontal", occlusion: "None", lighting: "Good" },
  },
  {
    id: "sunglasses",
    label: "Sunglasses Disguise — Low Confidence",
    image: "https://static.prod-images.emergentagent.com/jobs/88bcfbb5-0f75-48df-9f1d-e41b46909d27/images/73e5782dd778df194b0e82a184ef2df52fcafcbb7baa3f0fcc748c4d2e0a2383.png",
    score: 72,
    camera: "ANPR-CAM-14 | Highway NH-31, Toll Plaza",
    time: "16:05:48",
    location: "25.6120 N, 85.1580 E",
    action: "manual",
    description: "Subject wearing dark sunglasses partially occluding eye region. Confidence below threshold — requires manual operator verification.",
    features: { face_visible: "Partial", angle: "Slight Left", occlusion: "Sunglasses", lighting: "Outdoor" },
  },
  {
    id: "moustache",
    label: "Moustache & Cap — Low Confidence",
    image: "https://static.prod-images.emergentagent.com/jobs/88bcfbb5-0f75-48df-9f1d-e41b46909d27/images/01a1098356a88ea648046c1f3da675f5540c807eee163cf99c79b39a4c0bb467.png",
    score: 65,
    camera: "ANPR-CAM-22 | Market Area, Zone C",
    time: "10:22:15",
    location: "25.5941 N, 85.1376 E",
    action: "manual",
    description: "Subject has altered appearance with a moustache and cap. Significant feature deviation reduces confidence — flagged for manual action.",
    features: { face_visible: "Partial", angle: "Frontal", occlusion: "Cap + Moustache", lighting: "Market" },
  },
];

function ConfidenceBar({ score, animate }) {
  const isAuto = score >= 80;
  const color = isAuto ? "bg-emerald-500" : score >= 70 ? "bg-amber-500" : "bg-red-500";
  const textColor = isAuto ? "text-emerald-400" : score >= 70 ? "text-amber-400" : "text-red-400";

  return (
    <div className="w-full">
      <div className="flex justify-between mb-1">
        <span className="text-[10px] text-slate-500">Confidence Score</span>
        <span className={`text-sm font-bold ${textColor}`}>{animate ? score : 0}%</span>
      </div>
      <div className="h-2.5 bg-slate-800 rounded-full overflow-hidden">
        <motion.div
          className={`h-full ${color} rounded-full`}
          initial={{ width: 0 }}
          animate={{ width: animate ? `${score}%` : 0 }}
          transition={{ duration: 1.5, ease: "easeOut", delay: 0.5 }}
        />
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-[9px] text-slate-600">0%</span>
        <div className="relative flex-1 mx-2">
          <div className="absolute left-[80%] -top-0.5 w-px h-3 bg-slate-600" />
          <span className="absolute left-[80%] top-3 -translate-x-1/2 text-[8px] text-slate-600">80% threshold</span>
        </div>
        <span className="text-[9px] text-slate-600">100%</span>
      </div>
    </div>
  );
}

function ScanAnimation() {
  return (
    <div className="absolute inset-0 pointer-events-none z-10 overflow-hidden rounded-xl">
      <motion.div
        className="absolute left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent"
        initial={{ top: "0%" }}
        animate={{ top: "100%" }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
      />
      {/* Corner brackets */}
      <div className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-cyan-400/60 rounded-tl" />
      <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-cyan-400/60 rounded-tr" />
      <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-cyan-400/60 rounded-bl" />
      <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-cyan-400/60 rounded-br" />
    </div>
  );
}

export default function FaceMatchSimulator() {
  const [activeScenario, setActiveScenario] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle, scanning, analyzing, result
  const [alerts, setAlerts] = useState([]);
  const timerRef = useRef(null);

  const runScenario = (scenario) => {
    setActiveScenario(scenario);
    setPhase("scanning");
    clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      setPhase("analyzing");
      timerRef.current = setTimeout(() => {
        setPhase("result");
        setAlerts(prev => [{
          ...scenario,
          timestamp: new Date().toLocaleTimeString(),
        }, ...prev].slice(0, 10));
      }, 2000);
    }, 2500);
  };

  const reset = () => {
    setActiveScenario(null);
    setPhase("idle");
    clearTimeout(timerRef.current);
  };

  return (
    <div data-testid="face-match-simulator">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Live Face Recognition Simulation
        </h2>
        <p className="text-sm text-slate-400 mt-1">Select a scenario to simulate real-time ANPR camera detection</p>
      </div>

      {/* Scenario buttons */}
      <div className="flex flex-wrap gap-3 mb-6">
        {scenarios.map(s => (
          <button
            key={s.id}
            onClick={() => runScenario(s)}
            disabled={phase !== "idle" && phase !== "result"}
            data-testid={`scenario-btn-${s.id}`}
            className={`px-4 py-2 rounded-xl text-xs font-medium transition-all border ${
              activeScenario?.id === s.id
                ? s.action === "auto" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" : "bg-amber-500/10 border-amber-500/30 text-amber-400"
                : "bg-slate-900 border-slate-800 text-slate-400 hover:border-cyan-500/30"
            } disabled:opacity-50`}
          >
            {s.label} ({s.score}%)
          </button>
        ))}
        {phase !== "idle" && (
          <button onClick={reset} className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-800 text-slate-400 hover:text-white transition-all flex items-center gap-1.5" data-testid="reset-btn">
            <RotateCcw size={12} /> Reset
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Main display */}
        <div className="xl:col-span-2 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            {/* POI Reference */}
            <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4" data-testid="poi-reference">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3">Person of Interest — Reference</p>
              <div className="rounded-xl overflow-hidden aspect-square bg-slate-800 relative">
                <img src={POI_REF} alt="POI Reference" className="w-full h-full object-cover" />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                  <p className="text-xs font-semibold text-white">Subject Alpha</p>
                  <p className="text-[10px] text-slate-400">POI-2026-0847 | High Priority</p>
                </div>
              </div>
            </div>

            {/* Camera Feed */}
            <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4" data-testid="camera-feed">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3">
                {activeScenario ? activeScenario.camera : "ANPR Camera Feed"}
              </p>
              <div className="rounded-xl overflow-hidden aspect-square bg-slate-800 relative">
                {activeScenario ? (
                  <>
                    <img src={activeScenario.image} alt="Camera capture" className="w-full h-full object-cover" />
                    {(phase === "scanning" || phase === "analyzing") && <ScanAnimation />}
                    {/* Timestamp overlay */}
                    <div className="absolute top-2 left-2 bg-black/70 px-2 py-0.5 rounded text-[9px] text-red-400 font-mono">
                      REC {activeScenario.time}
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <Camera className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                      <p className="text-xs text-slate-600">Select a scenario to start</p>
                    </div>
                  </div>
                )}
                {phase === "result" && activeScenario && (
                  <div className={`absolute top-2 right-2 px-2 py-1 rounded-lg text-[10px] font-bold ${
                    activeScenario.action === "auto" ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" : "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                  }`}>
                    {activeScenario.score}% MATCH
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Analysis Panel */}
          <AnimatePresence mode="wait">
            {activeScenario && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6"
                data-testid="analysis-panel"
              >
                <div className="flex items-center gap-3 mb-4">
                  {phase === "scanning" && <><div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" /><span className="text-sm text-cyan-400 font-medium">Scanning face from camera feed...</span></>}
                  {phase === "analyzing" && <><div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" /><span className="text-sm text-amber-400 font-medium">Running facial recognition against POI database...</span></>}
                  {phase === "result" && activeScenario.action === "auto" && <><CheckCircle className="w-4 h-4 text-emerald-400" /><span className="text-sm text-emerald-400 font-medium">HIGH CONFIDENCE MATCH — Automatic Alert Triggered</span></>}
                  {phase === "result" && activeScenario.action === "manual" && <><AlertTriangle className="w-4 h-4 text-amber-400" /><span className="text-sm text-amber-400 font-medium">LOW CONFIDENCE — Flagged for Manual Review</span></>}
                </div>

                {phase === "result" && (
                  <>
                    <ConfidenceBar score={activeScenario.score} animate={phase === "result"} />

                    <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(activeScenario.features).map(([k, v]) => (
                        <div key={k} className="bg-slate-800/50 rounded-lg p-2.5">
                          <p className="text-[9px] text-slate-500 uppercase">{k.replace("_", " ")}</p>
                          <p className="text-xs font-medium text-slate-200 mt-0.5">{v}</p>
                        </div>
                      ))}
                    </div>

                    <p className="text-xs text-slate-400 mt-4 leading-relaxed">{activeScenario.description}</p>

                    {activeScenario.action === "auto" && (
                      <div className="mt-4 p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20 flex items-start gap-3">
                        <MapPin className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-semibold text-emerald-400">Geo-Tag & Tracking Initiated</p>
                          <p className="text-[10px] text-slate-400 mt-1">Location: {activeScenario.location} | Camera: {activeScenario.camera}</p>
                          <p className="text-[10px] text-slate-400">All connected cameras in 2km radius alerted for continuous tracking</p>
                        </div>
                      </div>
                    )}

                    {activeScenario.action === "manual" && (
                      <div className="mt-4 p-3 rounded-xl bg-amber-500/5 border border-amber-500/20 flex items-start gap-3">
                        <Eye className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-semibold text-amber-400">Awaiting Manual Verification</p>
                          <p className="text-[10px] text-slate-400 mt-1">Notification sent to Operator Station | Priority: Medium</p>
                          <p className="text-[10px] text-slate-400">Side-by-side comparison ready for human review</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Alert Log */}
        <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-5" data-testid="alert-log">
          <h3 className="text-sm font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Alert Feed
          </h3>
          {alerts.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-6 h-6 text-slate-700 mx-auto mb-2" />
              <p className="text-xs text-slate-600">No alerts yet. Run a scenario.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
              {alerts.map((a, i) => (
                <motion.div
                  key={`${a.id}-${i}`}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`p-3 rounded-xl border ${
                    a.action === "auto"
                      ? "bg-emerald-500/5 border-emerald-500/20"
                      : "bg-amber-500/5 border-amber-500/20"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[9px] font-bold uppercase ${a.action === "auto" ? "text-emerald-400" : "text-amber-400"}`}>
                      {a.action === "auto" ? "AUTO ALERT" : "MANUAL REVIEW"}
                    </span>
                    <span className="text-[9px] text-slate-600">{a.timestamp}</span>
                  </div>
                  <p className="text-xs text-slate-300 font-medium">{a.score}% Match — {a.label.split("—")[0].trim()}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">{a.camera}</p>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Camera = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);
